import { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AppContext } from "../providers/AppProvider";
import styled from "@emotion/styled";
import { gql } from "../api";
import CircularProgress from "@mui/material/CircularProgress";
import BackButton from "../components/BackButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LoadingButton from "../components/LoadingButton";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import { useNavigate } from "react-router-dom";

const LoadingWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
`;

const EditorStyle = {
  borderRadius: "4px",
  padding: "1rem",
  border: "1px solid #ccc",
};

const ToolbarStyle = {
  borderRadius: "4px",
  border: "1px solid #ccc",
};

export default function EditPost() {
  const { jwt } = useContext(AppContext);
  const { organizationId, id } = useParams();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const navigate = useNavigate();

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);

      try {
        const query = `
          query {
            posts (where: {id: $id}) {
              title
              body
              image
            }
          }
        `;

        const variables = { id };

        const result = await gql({ jwt, query, variables, organizationId });
        if (result.error) {
          throw result.error;
        }

        if (result.content.posts.length === 0) {
          throw new Error("Post not found");
        }

        const p = result.content.posts[0];
        setTitle(p.title);
        setImage(p.image);

        const contentBlock = htmlToDraft(p.body);

        setEditorState(
          EditorState.createWithContent(
            ContentState.createFromBlockArray(contentBlock.contentBlocks)
          )
        );
      } catch (err) {
        console.log("error", err);
      }

      setLoading(false);
    };

    loadPost();
  }, [id, jwt, organizationId]);

  const submit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError(false); // TODO: Handle errors

    // TODO: validate input data
    try {
      const query = `
        mutation {
          posts (update: $data, where: { id: $id }) {}
        }
      `;

      const body = draftToHtml(convertToRaw(editorState.getCurrentContent()));

      const variables = {
        id,
        data: {
          title,
          body,
          image,
        },
      };

      const result = await gql({ jwt, query, variables, organizationId });
      if (result.error) {
        throw result.error;
      }

      navigate(`/organizations/${organizationId}/posts/${id}`);
    } catch (err) {
      console.log("error", err);
    }

    setSaving(false);
  };

  return loading ? (
    <LoadingWrapper>
      <CircularProgress />
    </LoadingWrapper>
  ) : (
    <>
      <Title>
        <BackButton />
        <Typography component="h1" variant="h5" color="inherit">
          Edit Post
        </Typography>
      </Title>
      <Container component="main" maxWidth="sm">
        <Box component="form" onSubmit={submit} noValidate sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="title"
                id="title"
                label="Title"
                autoFocus
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="image"
                label="Image"
                name="image"
                value={image}
                onChange={(e) => {
                  setImage(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Editor
                editorStyle={EditorStyle}
                toolbarStyle={ToolbarStyle}
                editorState={editorState}
                onEditorStateChange={setEditorState}
              />
            </Grid>
          </Grid>
          <LoadingButton text="Save" loading={saving} />
          {error && (
            <Alert severity="error">
              Could not save post, try again later.
            </Alert>
          )}
        </Box>
      </Container>
    </>
  );
}
